import React, { useState, useEffect, useMemo } from 'react'
import useModals from '@/v2/hooks/useModals'
import Typography from '@/v2/components/common/Typography'
import Modal from '@/v2/components/common/Modal'
import validator from '@/v2/components/forms/Appointment/validator'
import formatBody from '@/v2/components/forms/Appointment/fromForm'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { errorsFromJson } from '@/v2/utils/forms'
import BaseForm from './BaseForm'

// Deep equality check function
const isEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2)
}
import { useLoadData } from './hooks/useLoadData'
import { useSaveNote } from './hooks/useSaveNote'
import RTAppointments from '@/v2/hooks/subscriptions/messages/RTAppointments'
import {
  useDeleteAppointment,
  useUpdateAppointment,
} from '@/v2/hooks/useAppointment'

const appointmentState = {
  patientType: 'registered',
  registeredPatient: {},
  doctorId: '',
  typeId: null,
  roomId: '',
  startsAtDate: null,
  startsAtTime: null,
  duration: '',
}

const EditAppointment = ({
  id,
  isOpen,
  onClose,
  onEdit,
  onCancel,
  onlyRead = false,
}) => {
  const { goTo, closeAll } = useModals()
  const {
    loading,
    error,
    types,
    rooms,
    doctors,
    appointment,
    patient,
  } = useLoadData(id)
  const { saveNote } = useSaveNote({ appointmentId: appointment?.id })
  const { updateAppointment } = useUpdateAppointment({ appointmentId: id })
  const { deleteAppointment } = useDeleteAppointment({ appointmentId: id })

  const { onOpenSnackbar } = useSnackbarContext()

  const initialValues = useMemo(() => {
    if (id) {
      return { ...appointment }
    }
    return appointmentState
  }, [appointment, id])
  const [values, setValues] = useState(initialValues)
  const [notes, setNotes] = useState([])
  const [formError, setFormError] = useState({})
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(loading)
  }, [loading])

  if (error)
    return (
      <Typography>
        Unable to load appointment details. Please refresh and try again.
      </Typography>
    )

  const filteredTypes = isLoading
    ? types
    : types.filter(type => type.isRemote === appointment.virtual)

  const handleChange = fields => setValues({ ...values, ...fields })

  const handleChangeNotes = newNote => {
    const noteExists = !!notes.find(note => note.id === newNote.id)
    const newNotes = noteExists
      ? notes.map(note => (note.id === newNote.id ? newNote : note))
      : [...notes, newNote]
    setNotes(newNotes)
  }

  const handleSubmit = async () => {
    const validErrors = validator(values)
    setFormError(validErrors || {})
    if (!validErrors) {
      try {
        const { ...otherFields } = formatBody(values)

        const response = await updateAppointment({
          id: appointment.id,
          ...otherFields,
          patientId: patient?.id,
        })

        if (response.errors) {
          onOpenSnackbar(
            'Unable to save appointment. Please check your input and try again.',
            'error'
          )
        } else {
          onOpenSnackbar('Updated appointment.')
        }

        const validNotes = notes.filter(
          note => !(!note.id && note.value === '')
        )
        // save each note
        validNotes.forEach(note => saveNote(note, appointment.id))

        onEdit && onEdit()

        goTo(`/v2/appointment/${appointment.id}`, {
          initialValues: { appointment: { ...response } },
        })

        if (window.analytics && window.analytics.track) {
          window.analytics.track('Updated appointment', {
            category: 'Clearworks',
            label: 'Appointment updated by staff',
            datetime: response.startsAt,
            appointmentType: response.appointmentType.title,
            room: response.room?.title,
          })
        }
      } catch (e) {
        console.error('handleSubmit error - appointment edit', e)
        onOpenSnackbar('Unable to save appointment. Please try again.', 'error')
      }
    }
  }

  const handleDelete = () => {
    try {
      deleteAppointment()

      const type = types.find(({ id }) => id === initialValues.typeId)
      const room = rooms.find(({ id }) => id === initialValues.roomId)

      if (window.analytics && window.analytics.track) {
        window.analytics.track('Deleted appointment', {
          category: 'Clearworks',
          label: 'Appointment deleted by staff',
          datetime: values.startsAtDate,
          appointmentType: type?.title,
          room: room?.title,
        })
      }
    } finally {
      onCancel && onCancel()
      // Close all modals to return to the main page
      closeAll()
      onOpenSnackbar('Appointment canceled.')
    }
  }

  const handleCancelAppointment = () =>
    confirm('Are you sure you want to cancel this appointment?') &&
    handleDelete()

  return (
    <Modal
      isOpen={isOpen}
      loading={isLoading || loading}
      title={onlyRead ? 'View Appointment' : 'Edit Appointment'}
      cancelLabel="Cancel Appointment"
      footer={onlyRead ? <div style={{ paddingTop: '20px' }}></div> : undefined}
      onConfirm={handleSubmit}
      onClose={({ header, footer }) => {
        if (header) {
          if (!isEqual(initialValues, values)) {
            return (
              confirm(
                'Are you sure you want to leave without saving changes?'
              ) && onClose()
            )
          } else {
            onClose()
          }
        }
        if (footer) {
          return handleCancelAppointment()
        }
      }}
    >
      <RTAppointments appointmentId={id} />
      <BaseForm
        edit
        onlyRead={onlyRead}
        error={formError}
        values={values}
        types={filteredTypes}
        rooms={rooms}
        doctors={doctors}
        onChange={handleChange}
        onChangeNotes={notes => handleChangeNotes(notes)}
      />
    </Modal>
  )
}

export default EditAppointment
